import { useEffect, useState } from 'react';

interface ToastProps {
  message: string;
  variant?: 'success' | 'error';
  isVisible: boolean;
  onDismiss: () => void;
  duration?: number;
}

type AnimationState = 'hidden' | 'entering' | 'visible' | 'exiting';

export function Toast({
  message,
  variant = 'success',
  isVisible,
  onDismiss,
  duration = 4000,
}: ToastProps) {
  const [animationState, setAnimationState] = useState<AnimationState>('hidden');

  // Handle visibility transitions
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;

    if (isVisible && (animationState === 'hidden' || animationState === 'exiting')) {
      // Transition to visible via entering state (use microtask to satisfy lint)
      timer = setTimeout(() => setAnimationState('entering'), 0);
    } else if (!isVisible && (animationState === 'visible' || animationState === 'entering')) {
      // Transition to hidden via exiting state
      timer = setTimeout(() => setAnimationState('exiting'), 0);
    }

    return () => clearTimeout(timer);
  }, [isVisible, animationState]);

  // Handle animation state transitions
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;

    if (animationState === 'entering') {
      // Quickly transition to visible
      timer = setTimeout(() => setAnimationState('visible'), 10);
    } else if (animationState === 'exiting') {
      // Wait for exit animation then hide
      timer = setTimeout(() => setAnimationState('hidden'), 300);
    }

    return () => clearTimeout(timer);
  }, [animationState]);

  // Handle auto-dismiss timer
  useEffect(() => {
    if (!isVisible) return;

    const dismissTimer = setTimeout(onDismiss, duration);
    return () => clearTimeout(dismissTimer);
  }, [isVisible, duration, onDismiss]);

  if (animationState === 'hidden') return null;

  const variantStyles = {
    success: 'bg-green-500 text-white',
    error: 'bg-red-500 text-white',
  };

  const icons = {
    success: (
      <svg className="w-5 h-5 animate-icon-spin-in" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
      </svg>
    ),
    error: (
      <svg className="w-5 h-5 animate-icon-spin-in" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
      </svg>
    ),
  };

  const showContent = animationState === 'visible' || animationState === 'entering';

  return (
    <div
      className={`
        fixed top-4 left-1/2 -translate-x-1/2 z-50
        flex items-center gap-3 px-4 py-3 rounded-xl shadow-lg
        transition-all duration-300 ease-out
        ${variantStyles[variant]}
        ${showContent ? 'translate-y-0 opacity-100' : '-translate-y-4 opacity-0'}
      `}
      role="alert"
    >
      {icons[variant]}
      <span className="text-sm font-medium">{message}</span>
      <button
        onClick={onDismiss}
        className="ml-2 p-1 hover:bg-white/20 rounded transition-colors"
        aria-label="Dismiss"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
}
